import { HighlightedLanguage, HIGHLIGHTED_LANGUAGES } from '../code';
import type { CodeElementProps } from './types';

/**
 * Determines whether a code element rendered by react-markdown is inline
 * (i.e. backtick-wrapped within a line) rather than a fenced code block.
 *
 * Uses two strategies:
 * 1. The legacy `inline` prop that react-markdown used to pass directly.
 * 2. Comparing start/end line positions from the mdast node, which is the
 *    approach recommended for newer versions of react-markdown.
 *
 * @see https://github.com/orgs/remarkjs/discussions/1426#discussioncomment-12548944
 */
export const isInlineCode = (props: CodeElementProps): boolean => {
  if ('inline' in props && Boolean(props.inline)) {
    return true;
  }
  return (
    !!props.node?.position &&
    props.node.position.start.line === props.node.position.end.line
  );
};

/**
 * Extracts the programming language identifier from a react-markdown code
 * element's `className` prop (e.g. `"language-typescript"` → `"typescript"`).
 *
 * Returns `undefined` when no language class is present or the className is
 * empty/undefined.
 *
 * @see https://github.com/remarkjs/react-markdown#use-custom-components-syntax-highlight
 */
export const extractLanguage = (
  className?: string
): HighlightedLanguage | undefined => {
  const match = /language-(\w+)/.exec(className || '');

  if (!match) return undefined;

  const language = match[1] as HighlightedLanguage;

  return HIGHLIGHTED_LANGUAGES.has(language) ? language : undefined;
};
